import { Typography } from "@mui/material";
import Paper from "apollo-react/components/Paper";
import Doctor2 from "apollo-react-icons/Doctor2";
import ChartPie from "apollo-react-icons/ChartPie";
import Table from "apollo-react-icons/Table";
import Route from "apollo-react-icons/Route";
import Building from "apollo-react-icons/Hospital";
import { Link, useNavigate } from "react-router-dom";
import ArrowRight from "apollo-react-icons/ArrowRight";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import "./styles.css";
import { useCurrentStageStatus } from "../../../hooks";
import { ROLES } from "../../../constants/roles";
import { STATUSES } from "../../../constants/statuses";
import { useTranslation } from "react-i18next";

const TrialCard = ({ requestProgress, route, isPI, onComplete, persona, showLoading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { status: currentStageStatus, loading: currentStageStatusLoading } = useCurrentStageStatus(
    requestProgress.request.id,
    persona
  );
  
  if (currentStageStatusLoading) {
    if (showLoading) {
      return <div>Loading...</div>;
    }
    return null;
  }

  const studyName = requestProgress.request?.study_name;
console.log("studyName =>", requestProgress);
  let navigateTo =
    currentStageStatus?.includes("-sendback") && persona === "customer"
      ? `${route}/send-back/${requestProgress.id}${studyName ? `?request_name=${encodeURIComponent(studyName)}` : ''}`
      : `${route}/details/${requestProgress.id}${studyName ? `?request_name=${encodeURIComponent(studyName)}` : ''}`;

  let status = requestProgress?.status;

  // Handle certificate license number click - navigate to Additional Documents tab
  const handleCertificateClick = () => {
    navigate(navigateTo, { state: { openTab: 3 } });
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "critical":
        return "critical";
      case "high":
        return "high";
      case "low":
        return "low";
      default:
        return "normal";
    }
  };

  const getValidatorType = (status) => {
    switch (status) {
      case STATUSES.ROLE_STATUSES[ROLES.pricing].ACTIVE:
        return "Pricing";
      case STATUSES.ROLE_STATUSES[ROLES.core_Validator].ACTIVE:
        return ROLES.core_Validator;
      case STATUSES.ROLE_STATUSES[ROLES.technical_validator].ACTIVE:
        return ROLES.technical_validator;
      case STATUSES.ROLE_STATUSES[ROLES.business_validator].ACTIVE:
        return ROLES.business_validator;
      case STATUSES.ROLE_STATUSES[ROLES.RA].ACTIVE:
        return ROLES.RA;
      default:
        return ""; // fallback for unknown status
    }
  };

  const getNextStageForSubmitted = (status) => {
    if (status == "submitted") {
      return "Technical";
    }
    if (status.includes("technical")) {
      return "Business";
    } else if (status.includes("business")) {
      return "Core";
    } else if (status.includes("core")) {
      return "Pricing";
    } else if (status.includes("pricing")) {
      return "Committee";
    } else if (status.includes("ra")) {
      return "";
    }
    return "";
  };

  const getRejectionSourceText = () => {
    if (!requestProgress.rejectionSource) return null;
    
    switch (requestProgress.rejectionSource) {
      case "me":
        return t("Rejected by me");
      case "core":
        return t("Rejected by Core Validator");
      case "both":
        return t("Rejected by me and Core Validator");
      default:
        return null;
    }
  };

  const getApprovalSourceText = () => {
    if (!requestProgress.approvalSource) return null;
    
    switch (requestProgress.approvalSource) {
      case "me":
        return t("Approved by me");
      case "core":
        return t("Approved by Core Validator");
      case "both":
        return t("Approved by me and Core Validator");
      default:
        return null;
    }
  };

  const getStatusBadge = () => {
    // Handle sendback scenarios
    if ((status.includes("sendback") && persona === "admin") ||
        (status.includes("sendback-active") && persona === "committee")) {
      return { text: "Sent back", className: "approved" };
    }
    
    if (status === "active-committee" && persona === "admin") {
      return { text: "Sent back to Assessment Reviewer", className: "approved" };
    }

    // Handle committee persona specific statuses
    if (persona === "committee") {
      // Direct status mapping for approved/rejected tabs
      if (requestProgress.status === "approved") {
        const approvalSourceText = getApprovalSourceText();
        return { 
          text: approvalSourceText || "Approved", 
          className: "approved" 
        };
      }
      if (requestProgress.status === "rejected") {
        const rejectionSourceText = getRejectionSourceText();
        return { 
          text: rejectionSourceText || "Rejected", 
          className: "rejected" 
        };
      }
      
      // Pricing tab statuses
      if (status.includes("pricing-active")) {
        return { text: "Under Review", className: "submitted" };
      }
      if (status.includes("pricing-approved")) {
        return { text: "Approved", className: "approved" };
      }
      if (status.includes("pricing-rejected")) {
        return { text: "Rejected", className: "rejected" };
      }
      
      // RA tab statuses
      if (status.includes("ra-active")) {
        return { text: "Under Review", className: "submitted" };
      }
      if (status.includes("ra-approved")) {
        return { text: "Approved", className: "approved" };
      }
      if (status.includes("ra-rejected")) {
        return { text: "Rejected", className: "rejected" };
      }
    }

    // General status handling for other personas
    if (status.includes("ra-approved")) {
      return { text: "Approved", className: "approved" };
    }
    if (status.includes("rejected")) {
      return { text: "Rejected", className: "rejected" };
    }
    
    if (status.includes("submitted")) {
      const nextStage = getNextStageForSubmitted(status);
      return { 
        text: nextStage ? `Under ${nextStage} Review` : "Under Review", 
        className: "submitted",
        showDays: true
      };
    }
    
    if (!requestProgress.priority && !requestProgress.remainingDays) {
      return { 
        text: `Under ${getValidatorType(requestProgress.status)} Review`, 
        className: "submitted" 
      };
    }
    
    // Priority-based status (fallback)
    return { 
      text: `Priority: ${requestProgress.priority}`, 
      className: `priority-badge ${getPriorityClass(requestProgress.priority)}`,
      showDays: true,
      isPriority: true
    };
  };

  const statusBadge = getStatusBadge();

  const handleComplete = async () => {
    const endpoint =
      persona === "admin"
        ? `/api/requests/complete-study/${requestProgress.request.id}`
        : `/api/requests/enduser/complete-study/${requestProgress.request.id}`;

    try {
      await axiosInstance.patch(endpoint);
      toast.success("Study marked as complete successfully.");
      if (onComplete) {
        await onComplete(requestProgress.id);
      }
    } catch (error) {
      console.error("Error completing study:", error);
      toast.error("Failed to mark study as complete.");
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear().toString().slice(-2);
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <Paper className="trial-card">
      <div className="header-container">
        <div className="header-left-container">
          {statusBadge.isPriority ? (
            <>
              <span className={statusBadge.className}>
                {statusBadge.text}
                {console.log("priority is showing", requestProgress.status)}
              </span>
              {statusBadge.showDays && (requestProgress.remainingDays || requestProgress.remainingDays === 0) && (
                <span className="days-badge">
                  {requestProgress.remainingDays ?? "-"} Days
                </span>
              )}
            </>
          ) : (
            <>
              <span className={`status-badge ${statusBadge.className}`}>
                {statusBadge.text}
              </span>
              {statusBadge.showDays && (requestProgress.remainingDays || requestProgress.remainingDays === 0) && (
                <span className="days-badge">
                  {requestProgress.remainingDays ?? "-"} Days
                </span>
              )}
            </>
          )}
        </div>
        {/* {status.includes("ra-approved") &&
          persona !== "committee" &&
          !isPI &&
          !requestProgress.request.study_completed &&
          requestProgress.request.IRB_Ref && (
            <button className="complete-button" onClick={handleComplete}>
              Complete
            </button>
          )} */}
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Doctor2 />
          </span>
          <Typography className="row-text-header" variant="body2">
            Request Name:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="body2">
          {requestProgress.request?.study_name}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ChartPie />
          </span>
          <Typography className="row-text-header" variant="body2">
            Reference Number:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="body2">
          {requestProgress.request.reference_number}
        </Typography>
      </div>

      {requestProgress.request.hospital && (
        <div className="info-row">
          <div className="info-label">
            <span className="icon-wrapper">
              <Building />
            </span>
            <Typography className="row-text-header" variant="body2">
              Hospital:
            </Typography>
          </div>
          <Typography className="row-text-data" variant="body2">
            {requestProgress.request.hospital}
          </Typography>
        </div>
      )}

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ArrowRight />
          </span>
          <Typography className="row-text-header" variant="body2">
            Stage Name:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="body2">
          {requestProgress.Stage.name}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Table />
          </span>
          <Typography className="row-text-header" variant="body2">
            Submission date:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="body2">
          {formatDate(requestProgress.request.createdAt)}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Route />
          </span>
          <Typography className="row-text-header" variant="body2">
            Last action date:
          </Typography>
        </div>
        {/* use updated at as some times the request progress gets updated */}
        <Typography className="row-text-data" variant="body2">
          {formatDate(requestProgress.updatedAt)}
        </Typography>
      </div>

      {/* Show certificate license number for approved requests */}
      {requestProgress.status?.includes("ra-approved") && requestProgress.request?.certificate_license_number && (
        <div className="info-row">
          <div className="info-label">
            <span className="icon-wrapper">
              <ArrowRight />
            </span>
            <Typography className="row-text-header" variant="body2">
              Certificate License Number:
            </Typography>
          </div>
          <Typography 
            className="row-text-data certificate-link" 
            variant="body2"
            onClick={handleCertificateClick}
          >
            {requestProgress.request.certificate_license_number}
          </Typography>
        </div>
      )}

      <div className="view-details">
        <Link to={navigateTo} className="link-style">
          View Details
        </Link>
      </div>
    </Paper>
  );
};

export default TrialCard;
